import React, { useState } from 'react';
import { X, Calendar, Users, Target, Clock, Flag, FileText, TrendingUp } from 'lucide-react';
import { UserAvatar } from '../UI/UserAvatar';
import { Sprint, User, Project } from '../../types';

interface SprintModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (sprintData: Omit<Sprint, 'id' | 'createdAt' | 'updatedAt'>) => void;
  users: User[];
  projects: Project[];
  sprint?: Sprint;
  currentUser: User;
}

export const SprintModal: React.FC<SprintModalProps> = ({
  isOpen,
  onClose,
  onSave,
  users,
  projects,
  sprint,
  currentUser
}) => {
  const [formData, setFormData] = useState({
    name: sprint?.name || '',
    goal: sprint?.goal || '',
    projectId: sprint?.projectId || projects[0]?.id || '',
    duration: sprint?.duration || 2,
    startDate: sprint?.startDate?.split('T')[0] || '',
    endDate: sprint?.endDate?.split('T')[0] || '',
    capacity: sprint?.capacity || undefined,
    capacityType: sprint?.capacityType || 'story-points',
    priority: sprint?.priority || 'medium',
    description: sprint?.description || '',
    teamMembers: sprint?.teamMembers || [],
    state: sprint?.state || 'future'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Sprint name is required';
    }

    if (!formData.goal.trim()) {
      newErrors.goal = 'Sprint goal is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    if (formData.teamMembers.length === 0) {
      newErrors.teamMembers = 'At least one team member is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const sprintData = {
      ...formData,
      boardId: '1', // Default board
      issues: sprint?.issues || [],
      createdBy: currentUser.id,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined
    };

    onSave(sprintData);
    onClose();
  };

  const calculateEndDate = (startDate: string, duration: number) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + (duration * 7));
    return end.toISOString().split('T')[0];
  };

  const handleStartDateChange = (date: string) => {
    setFormData({
      ...formData,
      startDate: date,
      endDate: calculateEndDate(date, formData.duration)
    });
  };

  const handleDurationChange = (duration: number) => {
    setFormData({
      ...formData,
      duration,
      endDate: formData.startDate ? calculateEndDate(formData.startDate, duration) : ''
    });
  };

  const toggleTeamMember = (userId: string) => {
    const isSelected = formData.teamMembers.includes(userId);
    setFormData({
      ...formData,
      teamMembers: isSelected
        ? formData.teamMembers.filter(id => id !== userId)
        : [...formData.teamMembers, userId]
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {sprint ? 'Edit Sprint' : 'Create New Sprint'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sprint Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Sprint 1, Authentication Sprint, Q1 Features"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sprint Goal *
                </label>
                <textarea
                  required
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.goal ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="What do you want to achieve in this sprint? Be specific and measurable."
                />
                {errors.goal && <p className="mt-1 text-sm text-red-600">{errors.goal}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Additional details about this sprint..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (Weeks) *
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => handleDurationChange(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={1}>1 Week</option>
                    <option value={2}>2 Weeks (Recommended)</option>
                    <option value={3}>3 Weeks</option>
                    <option value={4}>4 Weeks</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.startDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.endDate ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Capacity Type
                  </label>
                  <select
                    value={formData.capacityType}
                    onChange={(e) => setFormData({ ...formData, capacityType: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="story-points">Story Points</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sprint Capacity
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.capacity || ''}
                    onChange={(e) => setFormData({ ...formData, capacity: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Total ${formData.capacityType === 'story-points' ? 'story points' : 'hours'}`}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project *
                </label>
                <select
                  required
                  value={formData.projectId}
                  onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sprint Status
                </label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="future">Future</option>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Team Members * ({formData.teamMembers.length} selected)
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {users.map((user) => (
                    <label key={user.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.teamMembers.includes(user.id)}
                        onChange={() => toggleTeamMember(user.id)}
                        className="rounded text-blue-600 focus:ring-blue-500"
                      />
                      <UserAvatar
                        name={user.displayName}
                        src={user.avatarUrl}
                        className="w-8 h-8"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.displayName}</div>
                        <div className="text-xs text-gray-500">{user.emailAddress}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.teamMembers && <p className="mt-1 text-sm text-red-600">{errors.teamMembers}</p>}
              </div>

              {/* Sprint Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Sprint Summary</h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formData.duration} week{formData.duration !== 1 ? 's' : ''} duration</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4" />
                    <span>{formData.teamMembers.length} team member{formData.teamMembers.length !== 1 ? 's' : ''}</span>
                  </div>
                  {formData.capacity && (
                    <div className="flex items-center space-x-2">
                      <Target className="w-4 h-4" />
                      <span>{formData.capacity} {formData.capacityType === 'story-points' ? 'story points' : 'hours'}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <Flag className="w-4 h-4" />
                    <span className="capitalize">{formData.priority} priority</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <TrendingUp className="w-4 h-4" />
              <span>{sprint ? 'Update Sprint' : 'Create Sprint'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
